import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Display-only preview: deduct the checkout discount once from the selected total.
 * Variant prices come from Liquid through Horizon's existing priceContainer refresh.
 * @typedef {Object} PrepaidRefs
 * @property {HTMLElement} onlineAmount
 * @property {HTMLElement} regularAmount
 * @property {HTMLElement} quantityNote
 * @extends {Component<PrepaidRefs>}
 */
class DreamatixPrepaidOffer extends Component {
  requiredRefs = ['onlineAmount', 'regularAmount', 'quantityNote'];
  #controller;
  #section;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity, {
      signal: this.#controller.signal,
    });
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#afterVariant, {
      signal: this.#controller.signal,
    });
    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  #onQuantity = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.tagName !== 'QUANTITY-SELECTOR-COMPONENT') return;
    this.#update();
  };

  // Wait until all existing variant listeners have synchronized quantity constraints.
  #afterVariant = () => {
    queueMicrotask(() => {
      if (this.isConnected) this.#update();
    });
  };

  #update() {
    const input = this.#section?.querySelector('quantity-selector-component input[name="quantity"]');
    const quantity = input instanceof HTMLInputElement ? Number(input.value) : Number(this.dataset.minQuantity);
    const price = Number(this.dataset.unitPrice);
    const discount = Number(this.dataset.discount);
    if (!Number.isSafeInteger(quantity) || quantity < 1 || !Number.isSafeInteger(price) || !Number.isSafeInteger(discount)) return;

    const regular = price * quantity;
    const online = regular - discount;
    this.hidden = online <= 0;
    if (this.hidden) return;

    const format = (amount) => formatMoney(amount, '{{amount}}', 'INR').replace(/\.00$/, '');
    this.refs.onlineAmount.textContent = format(online);
    this.refs.regularAmount.textContent = `₹${format(regular)}`;
    this.refs.quantityNote.hidden = quantity === 1;
    this.refs.quantityNote.textContent = (this.dataset.quantityNote || '').replace('[quantity]', String(quantity));
  }
}

if (!customElements.get('dreamatix-prepaid-offer')) {
  customElements.define('dreamatix-prepaid-offer', DreamatixPrepaidOffer);
}
